import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, MagnifyingGlassPlus, FileText, Trash } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_LABELS = {
  personalausweis: 'Personalausweis',
  reisepass: 'Reisepass',
  fuehrerschein: 'Führerschein',
  auto: 'Ausweis',
};

const fileUrl = (storagePath) => `${API}/api/files/${storagePath}`;

const formatDate = (s, mode = 'full') => {
  if (!s) return '';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '';
  return mode === 'date'
    ? d.toLocaleDateString('de-DE')
    : d.toLocaleString('de-DE');
};

const IdScanGallery = ({ documents = [], customerId, canDelete = false, onChange }) => {
  const [active, setActive] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const scans = (documents || []).filter(Boolean);

  useEffect(() => {
    if (!active) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') setActive(null);
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  if (scans.length === 0) return null;

  const handleDelete = async (doc) => {
    if (!customerId || !doc?.photo_id) return;

    const label = DOC_LABELS[doc.doc_type] || 'Ausweis';
    const date = formatDate(doc.uploaded_at, 'date');

    if (!window.confirm(`${label} vom ${date} endgültig aus dem Archiv löschen?\nDies ist nicht rückgängig zu machen (DSGVO).`)) {
      return;
    }

    setDeletingId(doc.photo_id);

    try {
      await axios.delete(
        `${API}/api/customers/${customerId}/id-documents/${doc.photo_id}`,
        { withCredentials: true },
      );

      if (active?.photo_id === doc.photo_id) setActive(null);

      toast.success('Scan gelöscht');
      onChange && onChange();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Löschen fehlgeschlagen';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="card-swiss p-4 space-y-3" data-testid="id-scan-gallery">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[#111418]">
            Archivierte Ausweis-Scans
          </p>
          <p className="text-[11px] text-[#8A94A6]">
            {scans.length} Scan{scans.length === 1 ? '' : 's'} · Klick vergrößert
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {scans.map((doc, i) => {
          const isPdf = (doc.content_type || '').toLowerCase().includes('pdf');
          const src = fileUrl(doc.storage_path);
          const isDeleting = deletingId === doc.photo_id;

          return (
            <div
              key={doc.photo_id || i}
              className="relative group w-20 h-20"
            >
              <button
                type="button"
                onClick={() => setActive({ ...doc, index: i })}
                data-testid={`id-scan-thumb-${i}`}
                className="w-full h-full rounded border border-[#E1E4E8] bg-[#F5F7FA] overflow-hidden hover:border-[#111418] transition"
                title={`${DOC_LABELS[doc.doc_type] || 'Ausweis'} · ${formatDate(doc.uploaded_at, 'date')}`}
              >
                {isPdf ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#525866]">
                    <FileText weight="bold" size={22} />
                    <span className="text-[10px] mt-1">PDF</span>
                  </div>
                ) : (
                  <img
                    src={src}
                    alt={DOC_LABELS[doc.doc_type] || 'Ausweis-Scan'}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                )}

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                  <MagnifyingGlassPlus
                    weight="bold"
                    size={20}
                    className="text-white"
                  />
                </div>

                <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[9px] uppercase tracking-wide px-1 py-0.5 truncate">
                  {DOC_LABELS[doc.doc_type] || 'Ausweis'}
                </span>
              </button>

              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(doc);
                  }}
                  disabled={isDeleting}
                  data-testid={`id-scan-delete-${i}`}
                  aria-label="Scan löschen"
                  title="Scan löschen (DSGVO)"
                  className="absolute -top-1.5 -right-1.5 w-6 h-6 bg-white border border-[#E1E4E8] rounded-full flex items-center justify-center text-[#E60000] shadow-sm opacity-0 group-hover:opacity-100 hover:bg-[#E60000] hover:text-white hover:border-[#E60000] transition disabled:opacity-40 disabled:cursor-wait"
                >
                  <Trash weight="bold" size={12} />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {active && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Ausweis-Scan Vollbild"
          data-testid="id-scan-lightbox"
        >
          <div
            className="relative max-w-4xl max-h-[92vh] w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setActive(null)}
              className="absolute -top-2 -right-2 w-9 h-9 bg-white rounded-full flex items-center justify-center text-[#111418] hover:bg-[#F5F7FA] shadow-lg"
              data-testid="id-scan-lightbox-close"
            >
              <X weight="bold" size={18} />
            </button>

            <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between px-4 py-2 border-b border-[#E1E4E8] bg-[#F8F9FB]">
                <div>
                  <p className="text-sm font-semibold text-[#111418]">
                    {DOC_LABELS[active.doc_type] || 'Ausweis-Scan'}
                    <span className="text-[#8A94A6] font-normal">
                      {' · '}
                      Scan {active.index + 1} von {scans.length}
                    </span>
                  </p>

                  <p className="text-[11px] text-[#8A94A6]">
                    Hochgeladen am {formatDate(active.uploaded_at)}
                  </p>
                </div>

                <a
                  href={fileUrl(active.storage_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#111418] underline hover:text-[#E60000]"
                  data-testid="id-scan-lightbox-open"
                >
                  In neuem Tab öffnen
                </a>

                {canDelete && (
                  <button
                    type="button"
                    onClick={() => handleDelete(active)}
                    disabled={deletingId === active.photo_id}
                    data-testid="id-scan-lightbox-delete"
                    className="ml-3 text-xs inline-flex items-center gap-1 text-[#E60000] hover:underline disabled:opacity-50"
                  >
                    <Trash weight="bold" size={12} /> Scan löschen
                  </button>
                )}
              </div>

              <div className="bg-black/95 flex items-center justify-center max-h-[80vh] overflow-auto">
                {(active.content_type || '').toLowerCase().includes('pdf') ? (
                  <iframe
                    title="ID-Scan PDF"
                    src={fileUrl(active.storage_path)}
                    className="w-full h-[80vh] bg-white"
                  />
                ) : (
                  <img
                    src={fileUrl(active.storage_path)}
                    alt={DOC_LABELS[active.doc_type] || 'Ausweis'}
                    className="max-w-full max-h-[80vh] object-contain"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IdScanGallery;
